import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button, { ButtonProps } from '@alltrails/denali/components/Button';
import AlertDialog from '@alltrails/denali/components/AlertDialog';
import { Action, UserConnection, UserConnectionActionType, UserConnectionListKey } from '@alltrails/types/belay';
import SuccessToast from '@alltrails/shared/types/successToast';
import useConnectAction from '../../hooks/useConnectAction';
import getUserActionTitle from '../../utils/getUserActionTitle';
import { getButtonStyle, getConnectActionType } from './helpers';

type ConnectButtonProps = {
  actionType?: UserConnectionActionType;
  userConnection: UserConnection;
  listKey?: UserConnectionListKey;
  size?: ButtonProps['size'];
  fullWidth?: boolean;
  className?: string;
  onClick?: (actionType: UserConnectionActionType) => void;
  stopPropagation?: boolean;
  title?: string;
  setSuccessToast?: Dispatch<SetStateAction<SuccessToast>>;
  onMenuClick?: () => void;
  callback?: () => void;
};

const ConnectButton = ({
  actionType,
  userConnection,
  listKey,
  size = 'sm',
  fullWidth = false,
  className = '',
  onClick,
  stopPropagation = false,
  title,
  setSuccessToast,
  onMenuClick,
  callback
}: ConnectButtonProps) => {
  const intl = useIntl();
  const connectActionType = actionType || getConnectActionType(userConnection, listKey);
  const successToastText = useMemo(() => {
    if (!connectActionType || setSuccessToast === undefined) {
      return undefined;
    }

    const name = `${userConnection.user.firstName} ${userConnection.user.lastName}`;

    switch (connectActionType) {
      case UserConnectionActionType.Block:
        return <FormattedMessage defaultMessage="{name} blocked" values={{ name }} />;
      case UserConnectionActionType.Unblock:
        return <FormattedMessage defaultMessage="{name} unblocked" values={{ name }} />;
      case UserConnectionActionType.Follow:
        return <FormattedMessage defaultMessage="Following {name}" values={{ name }} />;
      case UserConnectionActionType.Unfollow:
      case UserConnectionActionType.RetractRequest:
        return <FormattedMessage defaultMessage="Unfollowed {name}" values={{ name }} />;
      default:
        return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userConnection, connectActionType]);

  const connectAction = useConnectAction(userConnection, connectActionType);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);

  const userActionTitle = useMemo(() => title || getUserActionTitle(intl, connectActionType), [intl, connectActionType, title]);

  if (!connectActionType || !connectAction) {
    return null;
  }

  const onConnectClick = () => {
    if (onClick) {
      onClick(connectActionType);
    }
    connectAction();
    if (successToastText && setSuccessToast) {
      setSuccessToast({ isVisible: true, text: successToastText });
    }
    if (callback) {
      callback();
    }
  };

  // Cast is safe because we know the action type is in the userConnection actions
  const action = userConnection.actions[connectActionType] as Action;

  const buttonStyle = getButtonStyle(connectActionType);

  const renderButton = (onButtonClick: () => void) => {
    return (
      <Button
        className={className}
        text={userActionTitle}
        ariaLabel={`${userActionTitle} ${userConnection.user.firstName} ${userConnection.user.lastName}`}
        onClick={onButtonClick}
        loading={action.requestState === 'loading'}
        width={fullWidth ? 'full' : 'auto'}
        size={size}
        testId="community-connect-button"
        variant={buttonStyle}
        stopPropagation={stopPropagation}
        allowWrap
      />
    );
  };

  if (connectActionType === UserConnectionActionType.Unfollow) {
    const closeDialog = () => setIsAlertDialogOpen(false);
    return (
      <>
        {renderButton(() => setIsAlertDialogOpen(true))}
        {isAlertDialogOpen && (
          <AlertDialog
            title={<FormattedMessage defaultMessage="Unfollow {firstName}?" values={{ firstName: userConnection.user.firstName }} />}
            body={<FormattedMessage defaultMessage="Their posts will no longer show up in your Community feed." />}
            buttons={{
              primary: {
                onClick: e => {
                  if (stopPropagation) {
                    e?.stopPropagation();
                  }
                  closeDialog();
                  onConnectClick();
                },
                text: <FormattedMessage defaultMessage="Unfollow" />,
                testId: 'connect-button-confirmation-confirm'
              },
              flat: {
                onClick: e => {
                  if (stopPropagation) {
                    e?.stopPropagation();
                  }
                  closeDialog();
                },
                text: <FormattedMessage defaultMessage="Cancel" />,
                testId: 'connect-button-confirmation-cancel'
              }
            }}
            onRequestClose={closeDialog}
            size="sm"
          />
        )}
      </>
    );
  }

  if (connectActionType === UserConnectionActionType.Block) {
    const closeDialog = () => setIsAlertDialogOpen(false);
    const renderAlertDialog = () => {
      if (onMenuClick) {
        onMenuClick();
      }
      setIsAlertDialogOpen(true);
    };
    return (
      <>
        {renderButton(renderAlertDialog)}
        {isAlertDialogOpen && (
          <AlertDialog
            title={<FormattedMessage defaultMessage="Block {firstName}?" values={{ firstName: userConnection.user.firstName }} />}
            body={
              <FormattedMessage defaultMessage="You’ll no longer be able to view each other’s content. They won’t be notified that you blocked them." />
            }
            buttons={{
              primary: {
                onClick: e => {
                  if (stopPropagation) {
                    e?.stopPropagation();
                  }
                  closeDialog();
                  onConnectClick();
                },
                text: <FormattedMessage defaultMessage="Block" />,
                testId: 'block-button'
              },
              flat: {
                onClick: e => {
                  if (stopPropagation) {
                    e?.stopPropagation();
                  }
                  closeDialog();
                },
                text: <FormattedMessage defaultMessage="Cancel" />,
                testId: 'block-button-confirmation-cancel'
              }
            }}
            onRequestClose={closeDialog}
            size="sm"
          />
        )}
      </>
    );
  }

  return renderButton(onConnectClick);
};

export default ConnectButton;
